import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Button,
  Select,
  message,
  Empty,
  Input,
  DatePicker,
} from "antd";
import { useIsMobile, useIsTablet } from "../../Hook";
import {
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  useActiveReturnTypesQuery,
  useActiveRefundTypeQuery,
  useOrderByIdQuery,
  useOrdersQuery,
  useCustomersQuery,
  useCreateOrderReturnMutation,
} from "../../Services";
import { useCart } from "../../Context";
import { RefundType, getPaymentMethodCode, PaymentMethod } from "../../Enums";
import { OrderReturnForm, isPaymentComplete } from "./OrderReturnForm";
import {
  PaymentOptionValue,
  formatPaymentDetails,
} from "../PaymentsOption/PaymentOption";
import { ReturnInvoiceDrawer } from "../ReturnInvoiceDrawer/ReturnInvoiceDrawer";
import { drawerTitle } from "../AppUi";

interface OrderReturnDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface ReturnItemRow {
  order_item_id: string;
  product_id: string;
  stock_id: string | null;
  product_name: string;
  unit?: string;
  batch_no?: string;
  price: number;
  quantity: number;
  max_quantity: number;
  return_type_id: string;
  stock_adjusted: number;
  refund_price: number;
  total_refund: number;
}

export const OrderReturnDrawer: React.FC<OrderReturnDrawerProps> = ({
  open,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { setExchangeDiscount } = useCart();
  const { data: paymentMethods = [] } = useActivePaymentMethodsQuery({enabled: open});
  const { data: bankAccounts = [] } = useActiveBankAccountsQuery({enabled: open});
  const { data: returnTypes = [] } = useActiveReturnTypesQuery({enabled: open});
  const { data: refundTypes = [] } = useActiveRefundTypeQuery({enabled: open,});
  const { data: customerResponse } = useCustomersQuery({enabled: open,});
  const customers = customerResponse?.data ?? [];
  const [searchInvoice, setSearchInvoice] = useState("");
  const [searchCustomerId, setSearchCustomerId] = useState<string | null>(null);
  const [searchDate, setSearchDate] = useState<any>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [returnItems, setReturnItems] = useState<ReturnItemRow[]>([]);
  const [selectedRefundMethodId, setSelectedRefundMethodId] = useState<string | null>(null);
  const [selectedRefundTypeId, setSelectedRefundTypeId] = useState<string | null>(null);
  const refundMethodId = selectedRefundMethodId ?? paymentMethods[0]?.id ?? null;
  const refundTypeId =
    selectedRefundTypeId ??
    refundTypes.find(
      (t: any) => String(t.id || t.code || "").toLowerCase() === "cash_ref",
    )?.id ??
    refundTypes[0]?.id ??
    null;

  const setRefundMethodId = setSelectedRefundMethodId;
  const setRefundTypeId = setSelectedRefundTypeId;

  const [prevOrderDetailsId, setPrevOrderDetailsId] = useState<string | null>(null);
  const [prevDefaultConditionId, setPrevDefaultConditionId] = useState<string>("");
  const [refundPayment, setRefundPayment] = useState<PaymentOptionValue>({});
  const [note, setNote] = useState("");
  const [cashBalance, setCashBalance] = useState(0);
  const [pendingReturn, setPendingReturn] = useState<{
    id: string;
    return_no: string;
  } | null>(null);

  const queryParams = useMemo(() => {
    const params: any = {
      page: 1,
      limit: 20,
    };
    if (searchInvoice.trim()) {
      params.search = searchInvoice.trim();
    }
    if (searchCustomerId) {
      params.customer_id = searchCustomerId;
    }
    if (searchDate) {
      params.start_date = searchDate
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      params.end_date = searchDate.endOf("day").format("YYYY-MM-DD HH:mm:ss");
    }
    return params;
  }, [searchInvoice, searchCustomerId, searchDate]);

  const { data: ordersData, isFetching: loadingOrders } = useOrdersQuery(queryParams, {enabled: open});

  const orderResults = ordersData?.data || [];

  const { data: orderDetails, isFetching: loadingOrderDetails } =
    useOrderByIdQuery(selectedOrderId || "", {
      enabled: open && !!selectedOrderId,
    });

  const paymentMethodsForPayout = useMemo(
    () =>
      paymentMethods.filter(
        (m: any) => m.code !== getPaymentMethodCode(PaymentMethod.CREDIT),
      ),
    [paymentMethods],
  );

  const defaultConditionId = useMemo(() => {
    const good = returnTypes.find(
      (t: any) => String(t.code || t.id || t.name).toLowerCase() === "good",
    );
    return good?.id || returnTypes[0]?.id || "";
  }, [returnTypes]);

  const itemsTotalRefund = useMemo(
    () =>
      returnItems.reduce(
        (sum, item) => sum + item.refund_price * item.quantity,
        0,
      ),
    [returnItems],
  );

  const dueAmount = useMemo(
    () => orderDetails?.due_amount ?? 0,
    [orderDetails],
  );

  const dueAdjustment = useMemo(
    () => Math.min(dueAmount, itemsTotalRefund),
    [dueAmount, itemsTotalRefund],
  );

  const refundable = useMemo(
    () => parseFloat(Math.max(0, itemsTotalRefund - dueAdjustment).toFixed(2)),
    [itemsTotalRefund, dueAdjustment],
  );

  const refundPm = useMemo(
    () => paymentMethods.find((m: any) => m.id === refundMethodId),
    [paymentMethods, refundMethodId],
  );

  const returnMutation = useCreateOrderReturnMutation();

  const refundTypeCode = useMemo(() => {
    const t = refundTypes.find((x: any) => x.id === refundTypeId);
    return String(t?.code || "").toLowerCase();
  }, [refundTypes, refundTypeId]);
  const isStoreCredit =
    refundTypeId === RefundType.STORE_CREDIT ||
    refundTypeCode === "store_credit";
  const isCustomerCredit = refundTypeCode === "credit";
  const isItemExchange =
    refundTypeId === RefundType.ITEM_EXCHANGE ||
    refundTypeCode === "item_exchange";

  const buildReturnItems = (order: any): ReturnItemRow[] => {
    const existingReturnMap: Record<string, number> = {};
    (order.returns || []).forEach((returnEntry: any) => {
      (returnEntry.items || []).forEach((item: any) => {
        existingReturnMap[item.order_item_id] =
          (existingReturnMap[item.order_item_id] || 0) + item.quantity;
      });
    });

    return (order.items || []).map((item: any) => {
      const alreadyReturned = existingReturnMap[item.id] ?? 0;
      const maxQuantity = Math.max(0, item.quantity - alreadyReturned);
      return {
        order_item_id: item.id,
        product_id: item.product_id,
        stock_id: item.stock_id || null,
        product_name: item.product_name || item.title || "",
        unit: item.unit || "",
        batch_no: item.batch_no || "",
        price: item.price || 0,
        quantity: 0,
        max_quantity: maxQuantity,
        return_type_id: defaultConditionId,
        stock_adjusted: item.stock_id ? 1 : 0,
        refund_price:
          item.sold_price !== undefined && item.sold_price !== null
            ? item.sold_price
            : item.price || 0,
        total_refund: 0,
      };
    });
  };

  if (orderDetails && orderDetails.id !== prevOrderDetailsId) {
    setPrevOrderDetailsId(orderDetails.id);
    const items = buildReturnItems(orderDetails);
    setReturnItems(items);
  }

  if (defaultConditionId && defaultConditionId !== prevDefaultConditionId) {
    setPrevDefaultConditionId(defaultConditionId);
    setReturnItems((prev) =>
      prev.map((item) =>
        item.return_type_id && item.return_type_id !== ""
          ? item
          : { ...item, return_type_id: defaultConditionId },
      ),
    );
  }

  const resetDrawer = () => {
    setSelectedOrderId(null);
    setReturnItems([]);
    setRefundMethodId(paymentMethods[0]?.id || null);
    setRefundTypeId(refundTypes[0]?.id || null);
    setRefundPayment({});
    setNote("");
    setCashBalance(0);
    setSearchInvoice("");
    setSearchCustomerId(null);
    setSearchDate(null);
  };

  const handleReturnQtyChange = (index: number, value: number | null) => {
    const qty =
      value === null
        ? 0
        : Math.max(0, Math.min(value, returnItems[index]?.max_quantity ?? 0));
    setReturnItems((prev) =>
      prev.map((item, idx) =>
        idx !== index
          ? item
          : {
              ...item,
              quantity: qty,
              total_refund: parseFloat((item.refund_price * qty).toFixed(2)),
            },
      ),
    );
  };

  const handleReturnTypeChange = (index: number, value: string) => {
    setReturnItems((prev) =>
      prev.map((item, idx) =>
        idx !== index
          ? item
          : {
              ...item,
              return_type_id: value,
            },
      ),
    );
  };

  const chosenOrder = orderDetails;
  const returnRows = returnItems.filter((item) => item.quantity > 0);
  const hasValidReturnTypes = returnRows.every((item) =>
    Boolean(item.return_type_id),
  );
  const needsRefundPayment =
    refundable > 0 && refundTypeId === RefundType.CASH_REF;
  const refundPaymentValid =
    !needsRefundPayment ||
    isPaymentComplete(refundPm, refundPayment, refundable);

  const canSubmit =
    Boolean(chosenOrder) &&
    returnRows.length > 0 &&
    hasValidReturnTypes &&
    refundPaymentValid;

  const buildPayDetail = (
    pm: any,
    pay: PaymentOptionValue,
  ): PaymentOptionValue | null => {
    if (!pm) return null;
    return formatPaymentDetails(pay, pm.code);
  };

  const handleSubmit = () => {
    if (!chosenOrder) {
      message.warning("Select an invoice before submitting a return.");
      return;
    }

    if (returnRows.length === 0) {
      message.warning("Select at least one item quantity to return.");
      return;
    }

    if (returnRows.some((item) => !item.return_type_id)) {
      message.warning("Select a return condition for each item.");
      return;
    }

    if (refundable > 0 && needsRefundPayment && !refundPaymentValid) {
      message.warning(
        "Complete the refund payment details (amount must equal the refundable amount).",
      );
      return;
    }

    if (refundable > 0 && isStoreCredit && !chosenOrder.customer_id) {
      message.warning("Store credit requires an invoice with a customer.");
      return;
    }

    const effectiveCashBalance = isStoreCredit
      ? Math.max(0, Math.min(cashBalance || 0, refundable))
      : 0;

    if (isStoreCredit && effectiveCashBalance < 0) {
      message.warning("Cash balance cannot be negative.");
      return;
    }

    if (refundable > 0 && isCustomerCredit && !chosenOrder.customer_id) {
      message.warning("Credit refund requires an invoice with a customer.");
      return;
    }

    const payloadItems = returnRows.map((item) => ({
      order_item_id: item.order_item_id,
      product_id: item.product_id,
      stock_id: item.stock_id,
      quantity: item.quantity,
      refund_price: item.refund_price,
      total_refund: item.total_refund,
      return_type_id: item.return_type_id,
      stock_adjusted: item.stock_adjusted,
    }));

    const payload: any = {
      order_id: chosenOrder.id,
      total_refunded: refundable,
      refund_type_id: refundable > 0 ? refundTypeId : null,
      refund_payment_method_id:
        refundable > 0 && needsRefundPayment ? refundMethodId : null,
      refund_payment:
        refundable > 0 && needsRefundPayment
          ? buildPayDetail(refundPm, refundPayment)
          : null,
      store_credit_amount:
        refundable > 0 && isStoreCredit
          ? parseFloat((refundable - effectiveCashBalance).toFixed(2))
          : null,
      cash_balance_amount:
        refundable > 0 && isStoreCredit ? effectiveCashBalance : null,
      note: note || null,
      items: payloadItems,
    };

    returnMutation.mutate(payload, {
      onSuccess: (res: any) => {
        if (res?.success) {
          message.success(res.message || "Return processed successfully.");
          if (isItemExchange && refundable > 0) {
            setExchangeDiscount(refundable);
          }
          if (isStoreCredit && effectiveCashBalance > 0) {
            setExchangeDiscount(effectiveCashBalance);
          }
          const retId = res?.data?.id ?? null;
          if (isStoreCredit && retId) {
            setPendingReturn({
              id: retId,
              return_no: res?.data?.return_no ?? "",
            });
          } else {
            resetDrawer();
            onClose();
          }
        } else {
          message.error(res?.message || "Failed to process order return.");
        }
      },
      onError: (error: any) => {
        message.error(error?.message || "Failed to process order return.");
      },
    });
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={() => {
          resetDrawer();
          onClose();
        }}
        width={isMobile || isTablet ? "100%" : 720}
        placement="right"
        title={drawerTitle(
          "Order Return",
          "Search invoices and select returned items.",
        )}
        footer={
          <div className="flex gap-2">
            <Button
              size="middle"
              className="flex-1 h-9 text-xs rounded-md border-gray-200 text-gray-500"
              onClick={() => {
                resetDrawer();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              size="middle"
              type="primary"
              disabled={!canSubmit}
              loading={returnMutation.isPending}
              onClick={handleSubmit}
              className="flex-1 h-9 text-xs font-semibold rounded-md"
            >
              Process Return
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {!selectedOrderId ? (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Invoice No
                  </span>
                  <Input
                    value={searchInvoice}
                    onChange={(e) => setSearchInvoice(e.target.value)}
                    placeholder="e.g. INV-001"
                    className="h-9 text-xs rounded-md"
                    allowClear
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Customer
                  </span>
                  <Select
                    value={searchCustomerId}
                    onChange={setSearchCustomerId}
                    placeholder="Select Customer"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    className="h-9 text-xs w-full"
                  >
                    {customers.map((c: any) => (
                      <Select.Option key={c.id} value={c.id}>
                        {c.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Order Date
                  </span>
                  <DatePicker
                    value={searchDate}
                    onChange={setSearchDate}
                    placeholder="Select date"
                    className="h-9 text-xs w-full rounded-md"
                    allowClear
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide px-1">
                  Matching Orders ({orderResults.length})
                </div>

                {loadingOrders ? (
                  <div className="text-center py-8">
                    <span className="text-xs text-gray-400">
                      Loading orders...
                    </span>
                  </div>
                ) : orderResults.length === 0 ? (
                  <Empty description="No matching orders found. Adjust your search criteria above." />
                ) : (
                  <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto pr-1">
                    {orderResults.map((order: any) => (
                      <div
                        key={order.id}
                        onClick={() => setSelectedOrderId(order.id)}
                        className="p-3 border border-gray-100 hover:border-indigo-500 hover:bg-indigo-50/20 rounded-xl cursor-pointer transition-all duration-200 flex justify-between items-center bg-white shadow-sm"
                      >
                        <div className="flex flex-col gap-0.5">
                          <span className="font-bold text-xs text-gray-800">
                            {order.order_no}
                          </span>
                          <span className="text-[10px] text-gray-400 font-medium">
                            Customer: {order.customer_name || "Walk-in"} • Date:{" "}
                            {order.order_date
                              ? order.order_date.split(" ")[0]
                              : ""}
                          </span>
                        </div>
                        <div className="text-right flex flex-col items-end">
                          <span className="text-xs font-extrabold text-indigo-600">
                            LKR{" "}
                            {Number(order.grand_total || 0).toLocaleString(
                              undefined,
                              { minimumFractionDigits: 2 },
                            )}
                          </span>
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                            {order.payment_status_name || "Unpaid"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center bg-indigo-50/50 border border-indigo-100 rounded-xl p-4">
                <div>
                  <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">
                    Active Return Order
                  </div>
                  <div className="text-sm font-extrabold text-indigo-800 mt-0.5">
                    {chosenOrder?.order_no} (
                    {chosenOrder?.customer_name || "Walk-in"})
                  </div>
                </div>
                <Button
                  size="small"
                  onClick={() => setSelectedOrderId(null)}
                  className="text-[10px] font-bold h-7 rounded-md border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                >
                  Change Order
                </Button>
              </div>

              <div className="space-y-4">
                <OrderReturnForm
                  orderDetails={orderDetails}
                  loadingOrderDetails={loadingOrderDetails}
                  refundTypeId={refundTypeId}
                  setRefundTypeId={setRefundTypeId}
                  refundTypes={refundTypes}
                  returnItems={returnItems}
                  returnTypes={returnTypes}
                  handleReturnQtyChange={handleReturnQtyChange}
                  handleReturnTypeChange={handleReturnTypeChange}
                  itemsTotalRefund={itemsTotalRefund}
                  refundable={refundable}
                  dueAmount={dueAmount}
                  paymentMethods={paymentMethodsForPayout}
                  bankAccounts={bankAccounts}
                  refundMethodId={refundMethodId}
                  setRefundMethodId={setRefundMethodId}
                  refundPayment={refundPayment}
                  setRefundPayment={setRefundPayment}
                  note={note}
                  setNote={setNote}
                  cashBalance={cashBalance}
                  setCashBalance={setCashBalance}
                />
              </div>
            </div>
          )}
        </div>
      </Drawer>
      <ReturnInvoiceDrawer
        open={!!pendingReturn}
        returnId={pendingReturn?.id || null}
        returnNo={pendingReturn?.return_no}
        creditOnly
        onClose={() => {
          setPendingReturn(null);
          resetDrawer();
          onClose();
        }}
      />
    </>
  );
};
